import ContentfulContent from "@cms/ContentfulContent"
import Button from "@components/buttons/Button"
import Helmet from "@components/Helmet"
import Layout from "@components/Layout"
import ProductBlockWithSwatches, {
  Container as ProductBlockContainer,
} from "@components/ProductBlockWithSwatches"
import ProductFilterMobile from "@components/ProductFilterMobile"
import { BreadcrumbSchema, CollectionPageSchema } from "@components/seo"
import SortDropdown, { SortContainer } from "@components/SortDropdown"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  h4Styles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import { useBaseContext } from "@context/BaseContext"
import useElevarEvents from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useMultiWarehouseInventory from "@hooks/useMultiWarehouseInventory"
import { RootState } from "@redux/store"
import { getCollectionBannerData, getIn } from "@utils/index"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import { useCollectionAnalytics } from "@views/hooks/useCollectionAnalytics"
import { useCollectionPagination } from "@views/hooks/useCollectionPagination"
import { useCollectionSorting } from "@views/hooks/useCollectionSorting"
import { useFilteredProducts } from "@views/hooks/useFilteredProducts"
import { useFormattedCollectionProducts } from "@views/hooks/useFormattedCollectionProducts"
import { useProductAvailability } from "@views/hooks/useProductAvailability"
import { useProductsFilters } from "@views/hooks/useProductsFilters"
import * as R from "ramda"
import React from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"

import Banner, { BannerTypes } from "./components/Banner"
import FilterBar from "./components/FilterBar"
import SideNav from "./components/SideNav"

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    border-top: solid 1px ${colors.dividerLineGray};
  }
`

const ProductList = styled.div<{ $displayBanner: boolean }>`
  ${({ $displayBanner }) => css`
    display: flex;
    margin-bottom: 86px;
    padding: 0;
    flex-wrap: wrap;
    justify-content: space-between;
    border-bottom: solid 1px ${colors.dividerLineGray};

    @media ${breakpoints.laptop} {
      width: calc(10 / 12 * 100vw);
      max-width: calc(100vw - 300px);
      min-height: 80vh;
      margin-bottom: 0;
      padding: ${$displayBanner
        ? "51px calc(1 / 12 * 100vw) 180px"
        : "113px calc(1 / 12 * 100vw) 180px"};
      align-content: flex-start;
      border: none;
    }
  `}
`

const ProductGrid = styled.div`
  justify-content: space-between;
  flex-wrap: wrap;
  display: flex;
  padding: 0;
  width: 100%;

  @media ${breakpoints.laptop} {
    align-items: stretch;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 46px 24px;
    margin-bottom: 46px;
  }

  ${ProductBlockContainer} {
    width: calc(50% - 0.5px);
    flex-grow: 0;
    flex-shrink: 0;
    flex-wrap: wrap;
    justify-content: space-between;
    border-top: solid 1px ${colors.dividerLineGray};

    @media ${breakpoints.laptop} {
      flex-grow: 0;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: space-between;
      width: initial;
      min-width: 0;
      padding: 0;
      margin: 0;
      border: none;
    }
  }
`

const Row = styled.div<{
  $frosmoMobilePadding?: boolean
  $noPadding?: boolean
}>`
  ${({ $frosmoMobilePadding, $noPadding }) => css`
    width: 100%;
    display: flex;
    padding: ${$noPadding && $frosmoMobilePadding
      ? "0 calc(1 / 8 * 100%) 0 20px"
      : $noPadding && !$frosmoMobilePadding
        ? "40px calc(1 / 8 * 100%) 0 20px"
        : $frosmoMobilePadding
          ? "0 0 40px 20px"
          : "0 calc(1 / 8 * 100%) 40px 20px"};
    align-items: center;
    justify-content: space-between;

    @media ${breakpoints.tablet} {
      padding: 0 0 48px 0;
    }

    @media ${breakpoints.laptop} {
      margin-bottom: 40px;
      padding: 0 24px 0 0;
    }

    ${SortContainer} {
      @media ${breakpoints.tablet} {
        transform: translateX(28px);
      }

      @media ${breakpoints.laptop} {
        transform: translateX(28px);
      }
    }
  `}
`

const Header = styled.h1<{ $displayBanner: boolean }>`
  ${({ $displayBanner }) => css`
    ${h4Styles}
    display: ${$displayBanner ? "none" : "inline-block"};
    max-width: 100%;
    flex-shrink: 0;

    @media ${breakpoints.tablet} {
      display: inline-block;
      max-width: calc(50% - (6 / 9 / 12 * 100vw));
    }

    @media ${breakpoints.laptop} {
      display: inline-block;
    }
  `}
`

const Subheader = styled.h1`
  ${smallcapsBold13Styles}
  display: inline-block;
  max-width: 100%;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    max-width: calc(50% - (6 / 9 / 12 * 100vw));
  }
`

const Divider = styled.div`
  width: 1px;
  background-color: ${colors.dividerLineGray};

  @media ${breakpoints.tablet} {
    display: none;
  }

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const NoMatchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  padding-top: 64px;

  @media ${breakpoints.laptop} {
    padding-top: 128px;
  }

  > button {
    max-width: 300px;
    width: max-content;
    margin-top: 26px;
  }
`

const H5 = styled.h5`
  ${h5Styles}
`

const Footer = styled.div`
  @media ${breakpoints.laptop} {
    margin-bottom: 96px;
    padding-left: calc(2 / 12 * 100vw);
  }

  p {
    font-size: 16px !important;
    line-height: 24px !important;
  }

  @media ${breakpoints.tablet} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }

  @media ${breakpoints.laptop} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }

  @media ${breakpoints.desktop} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }
`

const productListRef = React.createRef<HTMLDivElement>()
const emptyProductReference = []

const ProductsPage: any = ({
  automaticDiscounts,
  customerCountryCode,
  customerEmail,
  customerLocationFetched,
  data,
  location,
  pageContext,
}): any => {
  // BaseContext
  const { data: baseContext } = useBaseContext()
  const filterFunnel = baseContext?.filterFunnel || {}
  const globalData = baseContext?.globalData || { menuItems: [] }
  const menuData = baseContext?.menuData || {}

  const { onClickProductCard } = useEnhancedEcommerceEvents()
  const { elevarOnSelectItem } = useElevarEvents()

  const pinnedProducts: any = R.reverse(
    data?.contentfulGlobalSettings?.pinnedProducts || []
  )

  const allProducts: Array<any> = getIn(
    data,
    "allContentfulProduct.nodes",
    emptyProductReference
  )

  // Collect all variant SKUs for inventory fetch
  const allVariantSkus = React.useMemo(() => {
    const allVariants = allProducts.reduce((acc, product) => {
      const variants = getIn(product, "variants", [])
      const validVariants = variants.filter((variant: any) => {
        if (product.customPrint) {
          return (
            variant.device?.slug &&
            variant.device?.name &&
            variant.images?.length > 0
          )
        }
        return variant.images?.length > 0
      })
      return [...acc, ...validVariants]
    }, [])

    return allVariants.map(({ sku }) => sku)
  }, [allProducts])

  const { inventoryData } = useMultiWarehouseInventory(
    allVariantSkus,
    allVariantSkus
  )

  const customPrintProducts = React.useMemo(
    () => allProducts.filter((p) => p.customPrint),
    [allProducts]
  )

  const clearanceData = getIn(data, "contentfulClearanceSettings", {})
  const prohibitedProductRules = getIn(
    data,
    "contentfulGlobalSettings.prohibitedProductsByCountry",
    []
  )

  // Formatted products with variants, pricing, analytics data
  const formattedProducts = useFormattedCollectionProducts({
    allProducts,
    automaticDiscounts,
    customerCountryCode,
    customerLocationFetched,
    globalData,
    inventoryData,
    mode: "Products",
    onClickProductCard,
    onSelectItem: elevarOnSelectItem,
    pageContext,
  })

  // Product availability logic
  const { availablePrintedDesignVariants, notAvailableInCustomerCountry } =
    useProductAvailability({
      customerCountryCode,
      customerLocationFetched,
      formattedProducts,
      prohibitedProductRules,
    })

  // Filter state management
  const filters = useProductsFilters({
    clearanceData,
    customPrintProducts,
    formattedProducts,
    globalSettings: data?.contentfulGlobalSettings,
  })

  // Apply filters to get filtered product list
  const filteredProducts = useFilteredProducts({
    availablePrintedDesignVariants,
    filters: {
      brandFilters: filters.brandFilters,
      colorFilters: filters.colorFilters,
      devicesFilter: filters.devicesFilter,
      discountTierFilters: filters.discountTierFilters,
      seriesFilters: filters.seriesFilters,
      showBundleProducts: filters.showBundleProducts,
      showClearance: filters.showClearance,
      showInStock: filters.showInStock,
      showMagnetCompatibleProducts: filters.showMagnetCompatibleProducts,
      showPrintedDesigns: filters.showPrintedDesigns,
      subTypeFilters: filters.subTypeFilters,
    },
    formattedProducts,
    notAvailableInCustomerCountry,
  })

  // Sorting logic
  const isCustomCollection = React.useMemo(
    () => !!(pageContext.collectionSlug || pageContext.seriesSlug),
    [pageContext]
  )

  const { dropdownOptions, getSortedProducts } = useCollectionSorting({
    customerCountryCode,
    isCustomCollection,
    pinnedProducts,
  })

  const sortedProducts = React.useMemo(
    () => getSortedProducts(filteredProducts),
    [filteredProducts, getSortedProducts]
  )

  // Pagination
  const { renderedProductCount } = useCollectionPagination({
    formattedProducts: sortedProducts,
    productListRef,
  })

  // Analytics
  useCollectionAnalytics({
    customerEmail,
    formattedProducts: sortedProducts,
    inventoryData,
    location,
    pageContext,
  })

  // Rendered products for display
  const renderedProducts = React.useMemo(
    () => R.take(renderedProductCount, sortedProducts),
    [renderedProductCount, sortedProducts]
  )

  // Maestra-specific rendering split
  const [
    [renderedBeforeMaestra, renderedAfterMaestra],
    setRenderedWithMaestra,
  ] = React.useState<[Array<any>, Array<any>]>([[], []])

  React.useEffect(() => {
    const beforeTwelve = R.take(12, renderedProducts)
    const afterTwelve = R.drop(12, renderedProducts)
    setRenderedWithMaestra([beforeTwelve, afterTwelve])
  }, [renderedProducts.length])

  // Footer content logic
  let footerContentForDeviceType

  if (pageContext.brandSlug && pageContext.deviceTypeSlug) {
    const allCollectionFooters = getIn(
      data,
      "contentfulBrand.collectionFooters",
      []
    )
    footerContentForDeviceType = allCollectionFooters.find((textBlock) => {
      const referenceSlug = getIn(textBlock, "reference.slug")
      return referenceSlug === pageContext.deviceTypeSlug
    })
  }

  const footerContent =
    footerContentForDeviceType ||
    getIn(data, "contentfulDevice.collectionFooter") ||
    getIn(data, "contentfulDeviceType.collectionFooter") ||
    getIn(data, "contentfulProductSeries.collectionFooter") ||
    getIn(data, "footerContent")
  const footerContentWithLocation = footerContent && {
    ...footerContent,
    location,
  }

  // Banner display logic
  const bannerContentKey = pageContext?.collectionBannerKey
  const bannerContentObj = bannerContentKey ? getIn(data, bannerContentKey) : {}
  const bannerContentData = getCollectionBannerData(bannerContentObj)
  let displayBanner = true

  if (
    !bannerContentData.collectionBannerType ||
    !bannerContentData.bannerImage ||
    !bannerContentData.mobileBannerImage
  ) {
    displayBanner = false
  } else if (
    bannerContentData.collectionBannerType === BannerTypes.HEADER &&
    !bannerContentData.bannerHeader
  ) {
    displayBanner = false
  } else if (
    bannerContentData.collectionBannerType === BannerTypes.PARAGRAPH &&
    (!bannerContentData.paragraphHeader || !bannerContentData.paragraphBody)
  ) {
    displayBanner = false
  }

  // SEO data (defined after bannerContentData)
  // Use formattedProducts (available at build time) instead of sortedProducts (requires inventory data)
  const firstProductWithImage = formattedProducts.find(
    (product) => product?.variants?.[0]?.images?.[0]
  )
  const firstProductImage = firstProductWithImage?.variants?.[0]?.images?.[0]
  const bannerImage = bannerContentData?.bannerImage?.file?.url
  const collectionImage = firstProductImage || bannerImage

  const seoData = {
    description: pageContext.seoDescription || pageContext.description,
    image: collectionImage,
    keywords: pageContext.seoKeywords,
    title: pageContext.seoTitle || pageContext.title,
    url:
      typeof window !== "undefined"
        ? window.location.href
        : `https://www.urbanarmorgear.com${location?.pathname || ""}`,
  }

  const inventoryDataLoaded = React.useMemo(() => {
    return inventoryData && Object.keys(inventoryData).length > 0
  }, [inventoryData])

  return (
    <Layout data={data} location={location}>
      <Helmet {...seoData} />
      <CollectionPageSchema
        description={seoData.description}
        name={seoData.title || "Products"}
        numberOfItems={renderedProducts.length}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://www.urbanarmorgear.com/" },
          {
            name: seoData.title || "Products",
            url:
              typeof window !== "undefined"
                ? window.location.href
                : `https://www.urbanarmorgear.com${location?.pathname || ""}`,
          },
        ]}
      />
      <DesktopOnly>
        <Banner {...bannerContentData} />
      </DesktopOnly>
      <Content>
        <MobileAndTabletOnly>
          <ProductFilterMobile
            activeBrands={filters.brandFilters?.length || 0}
            activeColors={filters.colorFilters?.length || 0}
            activeDiscountTiers={filters.discountTierFilters?.length || 0}
            activeProductSeries={filters.seriesFilters?.length || 0}
            activeSubTypes={filters.subTypeFilters?.length || 0}
            brands={filters.formattedBrands || []}
            colors={filters.formattedColors || []}
            devices={filters.formattedDevices || []}
            discountTiers={filters.formattedDiscountTiers || []}
            hasBundleProducts={filters.hasBundleProducts}
            hasMagnetCompatibleProducts={filters.hasMagnetCompatibleProducts}
            hasPrintedDesigns={customPrintProducts.length > 0}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            isOpen={filters.showFilterMenu}
            onRequestClose={filters.closeFilterMenu}
            onResetFilters={filters.resetFilters}
            onToggleBundleProducts={filters.toggleBundleProducts}
            onToggleClearance={filters.toggleClearance}
            onToggleInStock={filters.toggleInStock}
            onToggleMagnetCompatibleProducts={
              filters.toggleShowMagnetCompatibleProducts
            }
            onTogglePrintedDesigns={filters.togglePrintedDesigns}
            productSeries={filters.formattedSeries || []}
            showBundleProducts={filters.showBundleProducts}
            showClearance={filters.showClearance}
            showInStock={filters.showInStock}
            showMagnetCompatibleProducts={filters.showMagnetCompatibleProducts}
            showPrintedDesigns={filters.showPrintedDesigns}
            subTypes={filters.formattedSubTypes || []}
          />
          <FilterBar
            filterCount={
              (filters.subTypeFilters?.length || 0) +
              (filters.colorFilters?.length || 0) +
              (filters.brandFilters?.length || 0) +
              (filters.seriesFilters?.length || 0) +
              (filters.discountTierFilters?.length || 0)
            }
            filters={getIn(data, "filterData.filters", {})}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            onFilterClick={filters.openFilterMenu}
          />
          <Banner {...bannerContentData} />
        </MobileAndTabletOnly>
        <DesktopOnly>
          <SideNav
            activeBrands={filters.brandFilters?.length || 0}
            activeColors={filters.colorFilters?.length || 0}
            activeDiscountTiers={filters.discountTierFilters?.length || 0}
            activeProductSeries={filters.seriesFilters?.length || 0}
            activeSubTypes={filters.subTypeFilters?.length || 0}
            brands={filters.formattedBrands || []}
            colors={filters.formattedColors || []}
            discountTiers={filters.formattedDiscountTiers || []}
            displayBanner={displayBanner}
            filterFunnel={filterFunnel}
            filters={getIn(data, "filterData.filters", {})}
            globalData={globalData}
            hasBundleProducts={filters.hasBundleProducts}
            hasMagnetCompatibleProducts={filters.hasMagnetCompatibleProducts}
            hasPrintedDesigns={customPrintProducts.length > 0}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            menuData={menuData}
            onResetFilters={filters.resetFilters}
            onToggleBundleProducts={filters.toggleBundleProducts}
            onToggleClearance={filters.toggleClearance}
            onToggleInStock={filters.toggleInStock}
            onToggleMagnetCompatibleProducts={
              filters.toggleShowMagnetCompatibleProducts
            }
            onTogglePrintedDesigns={filters.togglePrintedDesigns}
            productSeries={filters.formattedSeries || []}
            showBundleProducts={filters.showBundleProducts}
            showClearance={filters.showClearance}
            showInStock={filters.showInStock}
            showMagnetCompatibleProducts={filters.showMagnetCompatibleProducts}
            showPrintedDesigns={filters.showPrintedDesigns}
            subTypes={filters.formattedSubTypes || []}
          />
        </DesktopOnly>
        <ProductList $displayBanner={displayBanner} ref={productListRef}>
          <div data-popmechanic-embed="52564" />
          <Row $noPadding={displayBanner}>
            <Header $displayBanner={displayBanner}>
              {displayBanner ? "" : pageContext.seoHeader || pageContext.title}
            </Header>
            <SortDropdown {...dropdownOptions} />
          </Row>
          {!inventoryDataLoaded && (
            <NoMatchWrapper>
              <H5>Loading products...</H5>
            </NoMatchWrapper>
          )}
          {inventoryDataLoaded && R.isEmpty(renderedProducts) && (
            <NoMatchWrapper>
              <H5>No products match your filters</H5>
              <Button
                hideDash
                onClick={filters.resetFilters}
                text={`RESET FILTERS (${filters.filterCount})`}
              />
            </NoMatchWrapper>
          )}
          {!pageContext.clearance && !R.isEmpty(renderedProducts) && (
            <Row>
              <Subheader>All Results</Subheader>
            </Row>
          )}
          <ProductGrid>
            {renderedBeforeMaestra?.map((p: any, i) => (
              <React.Fragment key={p.slug}>
                <ProductBlockWithSwatches
                  colorFilters={filters.colorFilters || []}
                  customerCountryCode={customerCountryCode}
                  customPrint={p.customPrint}
                  hideMobileSwatches
                  images={p.images}
                  key={p.slug}
                  onClick={p.onClick}
                  productName={p.productName}
                  selectedDeviceSlug={
                    pageContext.deviceSlug ||
                    (filters.deviceNameSlugMap &&
                      filters.devicesFilter?.[0] &&
                      filters.deviceNameSlugMap[
                        filters.devicesFilter[0].label
                      ]) ||
                    undefined
                  }
                  slug={p.slug}
                  tags={p.tags}
                  variants={p.variants}
                />
                {i % 2 === 0 && <Divider />}
              </React.Fragment>
            ))}
          </ProductGrid>
          <ProductGrid>
            {renderedAfterMaestra?.length > 0 &&
              renderedAfterMaestra?.map((p: any, i) => (
                <React.Fragment key={p.slug}>
                  <ProductBlockWithSwatches
                    colorFilters={filters.colorFilters || []}
                    customerCountryCode={customerCountryCode}
                    customPrint={p.customPrint}
                    hideMobileSwatches
                    images={p.images}
                    onClick={p.onClick}
                    productName={p.productName}
                    selectedDeviceSlug={
                      pageContext.deviceSlug ||
                      (filters.deviceNameSlugMap &&
                        filters.devicesFilter?.[0] &&
                        filters.deviceNameSlugMap[
                          filters.devicesFilter[0].label
                        ]) ||
                      undefined
                    }
                    slug={p.slug}
                    tags={p.tags}
                    variants={p.variants}
                  />
                  {i % 2 === 0 && <Divider />}
                </React.Fragment>
              ))}
          </ProductGrid>
          <div data-popmechanic-embed="52570" />
        </ProductList>
      </Content>
      {footerContentWithLocation && (
        <Footer>
          <ContentfulContent content={[footerContentWithLocation]} />
        </Footer>
      )}
    </Layout>
  )
}

const mapState = (state: RootState) => ({
  automaticDiscounts: state.discounts.automaticDiscounts,
  customerCountryCode: state.customer.customerLocation.customerCountryCode,
  customerEmail: state.customer.data?.emailAddress?.emailAddress || "",
  customerLocationFetched: state.customer.customerLocationFetched,
})

export default connect(mapState)(ProductsPage)
